import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, Map } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CATEGORIES, subcategoriesFor } from "@/data/taxonomy";
import { useLang } from "@/lib/i18n";

const HEAD = "text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500";
const selectCls =
  "w-full appearance-none rounded-[12px] border border-neutral-300 bg-white px-4 py-3.5 text-[15px] font-semibold text-neutral-900 outline-none focus:border-neutral-900 transition-colors";

function Select({ label, value, onChange, placeholder, options = [] }) {
  return (
    <div>
      <div className={HEAD}>{label}</div>
      <div className="relative mt-2">
        <select value={value} onChange={(e) => onChange(e.target.value)} className={selectCls}>
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? o}
            </option>
          ))}
        </select>
        <ChevronDown className="w-5 h-5 text-neutral-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
}

/**
 * The advanced-search drawer, one step at a time: the six general categories
 * first, then the types inside the chosen one, then a neighborhood. Nothing is
 * mixed into one long menu, and nothing is multi-select — a category and one
 * type inside it is the pair the directory is stored in.
 *
 * Opening the drawer seeds it from what is already on screen, so a visitor
 * browsing Eat & Drink finds its types waiting for them.
 */
export default function FilterModal({ open, onClose, onApply, neighborhoods, category = "", subs, nb = "" }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [cat, setCat] = useState("");
  const [sub, setSub] = useState("");
  const [zone, setZone] = useState("");

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setCat(category || "");
      setSub(subs?.size === 1 ? Array.from(subs)[0] : "");
      setZone(nb || "");
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
    // Seeded once per opening — the caller's sets while the drawer is open are
    // only ever written when it applies.
     
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // A type only means anything inside the category that holds it, so switching
  // category drops whatever was picked under the old one.
  const pickCategory = (next) => {
    setCat(next);
    setSub("");
  };

  const reset = () => {
    setCat("");
    setSub("");
    setZone("");
  };

  const apply = () =>
    onApply(
      cat ? new Set([cat]) : new Set(),
      sub ? new Set([sub]) : new Set(),
      zone ? new Set([zone]) : new Set()
    );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end bg-black/45"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white w-full max-h-[88vh] rounded-t-[24px] flex flex-col overflow-hidden"
            initial={{ y: 30, opacity: 0.7 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-3 pb-4 bg-white shrink-0">
              <div className="w-10 h-1 bg-neutral-300 rounded mx-auto mb-3" />
              <div className="flex justify-between items-center">
                <h2 className="text-[24px] tracking-[-0.04em] font-black text-neutral-900">{t("filter.title")}</h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="w-9 h-9 rounded-full border border-neutral-200 bg-white grid place-items-center text-neutral-700 hover:bg-neutral-100"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <p className="text-[13px] text-neutral-500 mt-1">{t("filter.lead")}</p>
            </div>

            <div className="flex-1 overflow-auto px-5 pb-4 space-y-5">
              <Select
                label={t("filter.category")}
                value={cat}
                onChange={pickCategory}
                placeholder={t("filter.anyCategory")}
                options={CATEGORIES}
              />

              {/* The category's own types, one step below the category itself. */}
              {cat ? (
                <Select
                  label={t("filter.typeIn", { category: cat })}
                  value={sub}
                  onChange={setSub}
                  placeholder={t("filter.allIn", { category: cat })}
                  options={subcategoriesFor(cat)}
                />
              ) : (
                <p className="text-[13px] leading-[1.5] text-neutral-500">{t("filter.hint")}</p>
              )}

              <Select
                label={t("filter.neighborhood")}
                value={zone}
                onChange={setZone}
                placeholder={t("filter.allSanJuan")}
                options={neighborhoods.map((n) => ({ value: n.slug, label: n.name }))}
              />

              {(cat || sub || zone) && (
                <button type="button" onClick={reset} className="text-[13px] font-bold text-neutral-500 underline">
                  {t("filter.reset")}
                </button>
              )}
            </div>

            <div className="border-t border-neutral-200 p-4 bg-white shrink-0 space-y-2.5">
              <button
                type="button"
                onClick={() => { onClose(); navigate("/map"); }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white py-3 text-[14px] font-bold text-neutral-800 transition-colors hover:border-neutral-500 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036] focus-visible:ring-offset-2"
              >
                <Map aria-hidden="true" className="h-4 w-4" strokeWidth={1.9} />
                {lang === "es" ? "Abrir mapa" : "Open Map"}
              </button>
              <button
                type="button"
                onClick={apply}
                className="w-full rounded-full py-3.5 text-[14px] font-bold bg-black text-white hover:bg-neutral-800 transition-colors"
              >
                {lang === "es" ? "Ver resultados" : "Show results"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}